import { createGlobalStyle, css } from 'styled-components';
import { normalize } from 'styled-normalize';

const GlobalStyles = createGlobalStyle`
  ${normalize}
  
  html {
    box-sizing: border-box;
  }
  *,
  *::before,
  *::after {
    box-sizing: inherit;
  }
  body {
    font-family: UniversNextforHSBC, 'Helvetica Neue', Helvetica, Arial, sans-serif;
    color: ${({ theme }) => theme.colors.charcoal};
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    line-height: 1.5;
    margin: 0;
    overflow-x: hidden;
  }
  input,
  select,
  option {
    font-family: UniversNextforHSBC, 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }
  dd {
    margin: 0;
  }
  ol {
    margin: 0;
    padding: 0;
  }
  button,
  input,
  optgroup,
  select,
  textarea {
    font-family: inherit;
    font-size: 100%;
  }

  :root {
    --f0: 0px;
    --f1: 1px;
    --f2: 2px;
    --f3: 3px;
    --f4: 4px;
    --f5: 5px;
    --f6: 6px;
    --f7: 7px;
    --f8: 8px;
    --f9: 9px;
    --f10: 10px;
    --f11: 11px;
    --f12: 12px;
    --f13: 13px;
    --f14: 14px;
    --f15: 15px;
    --f16: 16px;
    --f17: 17px;
    --f18: 18px;
    --f19: 19px;
    --f20: 20px;
    --f21: 21px;
    --f22: 22px;
    --f23: 23px;
    --f24: 24px;
    --f25: 25px;
    --f26: 26px;
    --f27: 27px;
    --f28: 28px;
    --f29: 29px;
    --f30: 30px;
    --f31: 31px;
    --f32: 32px;
    --f33: 33px;
    --f34: 34px;
    --f35: 35px;
    --f36: 36px;
    --f37: 37px;
    --f38: 38px;
    --f39: 39px;
    --f40: 40px;
    --f41: 41px;
    --f42: 42px;
    --f43: 43px;
    --f44: 44px;

    --r0: 0px;
    --r1: 4px;
    --r2: 8px;
    --r3: 12px;
    --r4: 16px;
    --r5: 20px;
    --r6: 24px;
    --r7: 28px;
    --r8: 32px;
    --r9: 36px;
    --r10: 40px;
    --r11: 44px;
    --r12: 48px;


    --font-size-1: 33px;
    --font-size-2: 28px;
    --font-size-3: 23px;
    --font-size-4: 19px;
    --font-size-5: 16px;
    --font-size-6: 14px;
    --font-size-7: 12px;

    --font-weight-light: 300;
    --font-weight-regular: 400;
    --font-weight-medium: 600;

    ${({ theme: { breakpoints } }) => {
      return css`
        ${breakpoints.up(breakpoints.md)} {
          --r0: 4px;
          --r1: 8px;
          --r2: 12px;
          --r3: 16px;
          --r4: 20px;
          --r5: 24px;
          --r6: 28px;
          --r7: 32px;
          --r8: 36px;
          --r9: 40px;
          --r10: 48px;
          --r11: 52px;
          --r12: 56px;

          --font-size-1: 44px;
          --font-size-2: 35px;
          --font-size-3: 28px;
          --font-size-4: 22px;
          --font-size-5: 16px;
          --font-size-6: 14px;
          --font-size-7: 12px;
        }

        ${breakpoints.up(breakpoints.xl)} {
          --r0: 8px;
          --r1: 12px;
          --r2: 16px;
          --r3: 20px;
          --r4: 24px;
          --r5: 28px;
          --r6: 32px;
          --r7: 36px;
          --r8: 40px;
          --r9: 48px;
          --r10: 56px;
          --r11: 60px;
          --r12: 64px;

          --font-size-1: 57px;
          --font-size-2: 43px;
          --font-size-3: 32px;
          --font-size-4: 24px;
          --font-size-5: 16px;
          --font-size-6: 14px;
          --font-size-7: 12px;
        }
      `;
    }}

    --green-leaf: ${({ theme: { colors } }) => colors.greenLeaf};
    --HSBC-red: ${({ theme: { colors } }) => colors.HSBC_red};
    --black: ${({ theme: { colors } }) => colors.black};
    --white: ${({ theme: { colors } }) => colors.white};
    --light-pearl: ${({ theme: { colors } }) => colors.lightPearl};
    --pearl: ${({ theme: { colors } }) => colors.pearl};
    --silver: ${({ theme: { colors } }) => colors.silver};
    --pewter: ${({ theme: { colors } }) => colors.pewter};
    --charcoal: ${({ theme: { colors } }) => colors.charcoal};
    --red-error: ${({ theme: { colors } }) => colors.redError};
    --amber-warning: ${({ theme: { colors } }) => colors.amberWarning};
    --green: ${({ theme: { colors } }) => colors.green};
    --blue: ${({ theme: { colors } }) => colors.blue};
    --red-tint: ${({ theme: { colors } }) => colors.redTint};
    --amber-tint: ${({ theme: { colors } }) => colors.amberTint};
    --green-tint: ${({ theme: { colors } }) => colors.greenTint};
    --blue-tint: ${({ theme: { colors } }) => colors.blueTint};
    --dusk-pearl: ${({ theme: { colors } }) => colors.duskPearl};
    --jade: ${({ theme: { colors } }) => colors.jade};
    --emerald: ${({ theme: { colors } }) => colors.emerald};
    --amber: ${({ theme: { colors } }) => colors.amber};
    --ruby: ${({ theme: { colors } }) => colors.ruby};
    --lolite: ${({ theme: { colors } }) => colors.lolite};
    --sapphire: ${({ theme: { colors } }) => colors.sapphire};
    --dark-pearl: ${({ theme: { colors } }) => colors.darkPearl};
    --dark-jade: ${({ theme: { colors } }) => colors.darkJade};
    --dark-emerald: ${({ theme: { colors } }) => colors.darkEmerald};
    --dark-amber: ${({ theme: { colors } }) => colors.darkAmber};
    --dark-ruby: ${({ theme: { colors } }) => colors.darkRuby};
    --dark-lolite: ${({ theme: { colors } }) => colors.darkLolite};
    --dark-sapphire: ${({ theme: { colors } }) => colors.darkSapphire};

    --tundora: ${({ theme: { colors } }) => colors.tundora};
  }

  ${(props) => props.theme.extensions?.base?.global}
`;

export default GlobalStyles;
